"use client";

import { useEffect, useState } from "react";

type State =
  | { step: "loading" }
  | { step: "signed_in"; email: string }
  | { step: "form" }
  | { step: "link_sent"; email: string }
  | { step: "not_found"; email: string };

export function CancelClient() {
  const [state, setState] = useState<State>({ step: "loading" });
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If they're already signed in with a subscription, skip straight to the portal button
    fetch("/api/subscription")
      .then((r) => r.json())
      .then((data) => {
        if (data?.email && data?.subscription) {
          setState({ step: "signed_in", email: data.email });
        } else {
          setState({ step: "form" });
        }
      })
      .catch(() => setState({ step: "form" }));
  }, []);

  const openPortal = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/portal", { method: "POST" });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || "Could not open the billing portal.");
      }
    } catch {
      setError("Could not open the billing portal.");
    } finally {
      setBusy(false);
    }
  };

  const sendLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const target = email.trim().toLowerCase();
    try {
      const res = await fetch("/api/restore", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: target }),
      });
      if (res.ok) {
        setState({ step: "link_sent", email: target });
      } else if (res.status === 404) {
        setState({ step: "not_found", email: target });
      } else {
        const data = await res.json().catch(() => null);
        setError(data?.error || "Something went wrong. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const card = "bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6";

  if (state.step === "loading") {
    return <div className={card}><p className="text-sm text-slate-500 dark:text-slate-400">Checking your account&hellip;</p></div>;
  }

  if (state.step === "signed_in") {
    return (
      <div className={card}>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
          Signed in as <strong>{state.email}</strong>. The billing portal lets you
          cancel, and also shows invoices and payment history.
        </p>
        <button
          onClick={openPortal}
          disabled={busy}
          className="w-full px-4 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-medium rounded-lg cursor-pointer"
        >
          {busy ? "Opening…" : "Open billing portal to cancel"}
        </button>
        {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>
    );
  }

  if (state.step === "link_sent") {
    return (
      <div className={card}>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          We&apos;ve emailed a secure sign-in link to <strong>{state.email}</strong>.
          Click it, then come back to this page — you&apos;ll see a cancel button.
          The link expires in 15 minutes.
        </p>
      </div>
    );
  }

  if (state.step === "not_found") {
    return (
      <div className={card}>
        <p className="text-sm text-slate-700 dark:text-slate-200 font-medium mb-2">
          No subscription found under {state.email}
        </p>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
          Subscriptions are tied to the email you entered on the <em>checkout</em>{" "}
          page, which can differ from the one you sign in with. Check your card
          statement or the Stripe receipt email for the address you used, then
          try again. If you can&apos;t find it, forward your receipt to{" "}
          <a href="mailto:support@ytranscript.com" className="text-purple-600 dark:text-purple-400 hover:underline">
            support@ytranscript.com
          </a>{" "}
          and we&apos;ll cancel it manually.
        </p>
        <button
          onClick={() => setState({ step: "form" })}
          className="text-sm text-purple-600 dark:text-purple-400 hover:underline cursor-pointer"
        >
          Try a different email
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={sendLink} className={card}>
      <label htmlFor="cancel-email" className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
        Email you used at checkout
      </label>
      <input
        id="cancel-email"
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white mb-4"
      />
      <button
        type="submit"
        disabled={busy}
        className="w-full px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-medium rounded-lg cursor-pointer"
      >
        {busy ? "Sending…" : "Email me a cancellation link"}
      </button>
      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
        We&apos;ll send a secure sign-in link — no password needed.
      </p>
    </form>
  );
}
